import React from "react";
import { toast } from "react-hot-toast";
import UpdateToast from "../components/UpdateToast";

const VERSION_URL = "/version.json";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const IDLE_RELOAD_MS = 30 * 1000;
const INTERACTION_EVENTS = ["mousemove", "keydown", "click", "touchstart"];

let runtimeBuildId = null;
let notified = false;
let idleTimer = null;
let intervalId = null;

function getRuntimeBuildId() {
  const fromWindow =
    typeof window !== "undefined" ? window.__APP_BUILD_ID__ : null;
  // CRA leaves the literal "%REACT_APP_BUILD_ID%" in place when the env var
  // isn't set (e.g. fresh clone running `npm start` without a prior build).
  // Treat that as "no build id" and skip the check entirely.
  if (!fromWindow || fromWindow.startsWith("%")) return null;
  return fromWindow;
}

async function fetchServerBuildId() {
  try {
    const res = await fetch(VERSION_URL, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.buildId ? data.buildId : null;
  } catch {
    return null;
  }
}

function clearIdleTimer() {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

function armIdleTimer() {
  clearIdleTimer();
  idleTimer = setTimeout(() => {
    window.location.reload();
  }, IDLE_RELOAD_MS);
}

function handleUserInteraction() {
  if (idleTimer) armIdleTimer();
}

function unregisterServiceWorkers() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
  }
}

function performRefresh() {
  clearIdleTimer();
  window.location.reload();
}

function showUpdateToast() {
  toast.custom(
    (t) => React.createElement(UpdateToast, { t, onRefresh: performRefresh }),
    { duration: Infinity, position: "bottom-right" }
  );
}

function handleVersionMismatch() {
  if (notified) return;
  notified = true;

  unregisterServiceWorkers();
  showUpdateToast();
  armIdleTimer();

  INTERACTION_EVENTS.forEach((evt) =>
    window.addEventListener(evt, handleUserInteraction, { passive: true })
  );

  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

async function checkVersion() {
  if (notified || !runtimeBuildId) return;
  const serverBuildId = await fetchServerBuildId();
  if (!serverBuildId) return;
  if (serverBuildId !== runtimeBuildId) handleVersionMismatch();
}

export function startVersionCheck() {
  // Skip on local dev — every `npm start` rewrites public/version.json with a
  // fresh buildId, and stale browser tabs from earlier dev sessions hold an
  // older __APP_BUILD_ID__, so the polling would falsely flag a "new version"
  // and the toast would re-appear after each refresh. Only run for production
  // bundles (which is what every deployed environment ships).
  if (process.env.NODE_ENV !== "production") return;
  if (intervalId !== null) return;
  runtimeBuildId = getRuntimeBuildId();
  if (!runtimeBuildId) return;

  checkVersion();
  intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);
  window.addEventListener("focus", checkVersion);
}
